
import React, { useState } from 'react';
import { MapPin, AlertTriangle, Map } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface MapErrorStateProps {
  errorMessage: string;
  onManualLocationSet?: (location: { lat: number; lng: number; name: string }) => void;
  onUseFallbackMap?: () => void;
}

const MapErrorState: React.FC<MapErrorStateProps> = ({ 
  errorMessage,
  onManualLocationSet,
  onUseFallbackMap
}) => {
  const [showManualInput, setShowManualInput] = useState(false);
  const [manualLat, setManualLat] = useState('');
  const [manualLng, setManualLng] = useState('');
  const [locationName, setLocationName] = useState('');
  
  // Extract the site URL to be authorized from the error message if it exists
  const siteUrlMatch = errorMessage.match(/Your site URL to be authorized: (https:\/\/[^\s]+)/);
  const siteUrlToAuthorize = siteUrlMatch ? siteUrlMatch[1] : null;

  const handleManualLocationSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const lat = parseFloat(manualLat);
    const lng = parseFloat(manualLng);
    
    if (isNaN(lat) || isNaN(lng)) {
      return; // Invalid input
    }
    
    if (onManualLocationSet) {
      onManualLocationSet({
        lat,
        lng,
        name: locationName || `Location (${lat.toFixed(4)}, ${lng.toFixed(4)})`
      });
    }
  };

  return (
    <div className="h-full flex items-center justify-center bg-gray-100 dark:bg-gray-800">
      <div className="text-center p-6 max-w-md bg-white dark:bg-gray-700 rounded-lg shadow-lg">
        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium mb-2">Map Loading Error</h3>
        <p className="text-sm text-muted-foreground mb-4">
          {errorMessage.includes("RefererNotAllowedMapError") 
            ? "Your site URL is not authorized to use this Google Maps API key." 
            : errorMessage}
        </p>
        
        {siteUrlToAuthorize && (
          <Alert variant="destructive" className="mb-4 text-left">
            <AlertDescription>
              <p className="mb-2">You need to authorize this URL in Google Cloud Console:</p>
              <code className="bg-gray-100 dark:bg-gray-800 p-1 rounded block overflow-x-auto text-xs">
                {siteUrlToAuthorize}
              </code>
            </AlertDescription>
          </Alert>
        )}
        
        {onUseFallbackMap && (
          <Button 
            onClick={onUseFallbackMap}
            className="w-full mb-4 flex items-center justify-center gap-2"
            size="lg"
          >
            <Map className="h-4 w-4" />
            Use Alternative Map
          </Button>
        )}
        
        <div className="text-sm text-muted-foreground mb-4">
          <p>This issue is usually caused by:</p>
          <ul className="list-disc text-left ml-6 mt-2">
            <li>Google Maps API key restrictions</li>
            <li>Domain not being authorized</li>
            <li>Missing billing on Google Cloud Platform</li>
          </ul>
        </div>
        
        {!showManualInput ? (
          <div className="space-y-2">
            {onManualLocationSet && (
              <Button 
                onClick={() => setShowManualInput(true)}
                variant="outline"
                className="w-full flex items-center justify-center gap-2"
              >
                <MapPin className="h-4 w-4" />
                Enter Location Manually
              </Button>
            )}
            
            <Button 
              onClick={() => window.location.reload()}
              variant="secondary"
              className="w-full flex items-center justify-center gap-2"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Try Again
            </Button>
          </div>
        ) : (
          <form onSubmit={handleManualLocationSubmit} className="space-y-3 text-left">
            <div>
              <Label htmlFor="location-name">Location Description</Label>
              <Input
                id="location-name"
                placeholder="e.g., Central Park, New York"
                value={locationName}
                onChange={(e) => setLocationName(e.target.value)}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="manual-lat">Latitude</Label>
                <Input
                  id="manual-lat"
                  placeholder="e.g., 40.7128"
                  value={manualLat}
                  onChange={(e) => setManualLat(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="manual-lng">Longitude</Label>
                <Input
                  id="manual-lng"
                  placeholder="e.g., -74.0060"
                  value={manualLng}
                  onChange={(e) => setManualLng(e.target.value)}
                  required
                />
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowManualInput(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="flex-1"
                disabled={!manualLat || !manualLng}
              >
                Set Location
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default MapErrorState;
